import { StatusNoIcon, StatusYesIcon } from "@components/icon";
import { ISelectItem } from "@components/inputs/select/Select.types";
import { SwitchType } from "@components/inputs/switch/Switch";
import { ifAny, IGetValueArgs, not, TValidatorFnResult } from "@components/smart/FieldInfo";
import {
    AccountDefFormatter,
    getAccountDef,
    getCommonFilterDefs,
    getCommonTableColumnsDefs,
    withDisplayName
} from "@components/smart/GeneralFieldDefinition";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { ISummaryItem } from "@components/smart/smartSummaryItem/SmartSummaryItem";
import { TFormatterFn } from "@components/smart/smartTable/SmartTable.utils";
import { TCellValue } from "@components/table";
import {
    AccountEntity,
    BalanceSheetLayoutEntity,
    EntitySetName,
    EntityTypeName,
    FinancialReportLayoutEntity,
    FinancialReportLayoutSectionEntity,
    IAccountEntity,
    IChartOfAccountsEntity,
    IFinancialReportLayoutSectionEntity,
    IncomeStatementLayoutEntity
} from "@odata/GeneratedEntityTypes";
import {
    AccountActivityTypeCode,
    AccountCategoryCode,
    AccountTypeCode,
    FiscalYearStatusCode,
    ReportSectionTypeCode,
    TaxApplicabilityCode
} from "@odata/GeneratedEnums";
import { IFormatOptions } from "@odata/OData.utils";
import {
    isIncomeStatementCategory,
    isNonProfitCompanyWithTemplateFallback
} from "@pages/chartOfAccountsTemplates/ChartOfAccountsTemplates.utils";
import { isNonProfitCompany } from "@utils/CompanyUtils";
import { isNotDefined, isObjectEmpty } from "@utils/general";
import i18next from "i18next";
import React from "react";
import { DefaultTheme } from "styled-components";
import { ValidationError } from "yup";

import Text from "../../components/text";
import {
    BANK_ACCOUNT_BALANCE_SHEET_ACCOUNT_PREFIX,
    CASH_BOXES_BALANCE_SHEET_ACCOUNT_PREFIXES,
    DASH_CHARACTER
} from "../../constants";
import { IAppContext } from "../../contexts/appContext/AppContext.types";
import {
    BasicInputSizes,
    CacheStrategy,
    FastEntryInputSizes,
    FieldType,
    FieldVisibility,
    IconSize,
    LabelStatus,
    NavigationSource,
    Sort,
    TextAlign,
    ValidatorType
} from "../../enums";
import { ColoredText } from "../../global.style";
import { TValue } from "../../global.types";
import { Model } from "../../model/Model";
import BindingContext, { createPath, IEntity } from "../../odata/BindingContext";
import { getUtcDayjs } from "../../types/Date";
import { IFormDef } from "../../views/formView/Form";
import { FormStorage } from "../../views/formView/FormStorage";
import { ISplitPageTableDef } from "../../views/table/TableView.utils";
import { setDefByEntityType } from "../getDefByEntityType";
import { getItemBreadCrumbsText, IDefinition, TFieldDefinition } from "../PageUtils";
import {
    CATEGORY_SELECT_PATH,
    CategoryItems,
    composeCategory,
    IChartOfAccountsFormCustomData
} from "./ChartOfAccounts.utils";
import ChartOfAccountsFormView from "./ChartOfAccountsFormView";

const DISABLED_PARENT_PREFIX = [...CASH_BOXES_BALANCE_SHEET_ACCOUNT_PREFIXES, ...BANK_ACCOUNT_BALANCE_SHEET_ACCOUNT_PREFIX];

export const accountCannotBeParent = (account: IAccountEntity): boolean => {
    return !!(!account || account.Parent?.Id);
};

export const typeFormatter = (val: TValue, { entity }: IFormatOptions<IAccountEntity>): TCellValue => {
    const title = val as string;
    const type = (entity.Type?.Code ?? entity.TypeCode) as AccountTypeCode;
    let color: keyof DefaultTheme;
    switch (true) {
        case [AccountTypeCode.Assets, AccountTypeCode.Income].includes(type):
            color = "C_SEM_text_good";
            break;
        case [AccountTypeCode.Liabilities, AccountTypeCode.Expense].includes(type):
            color = "C_SEM_text_warning";
            break;
    }
    const iconInstance = <ColoredText color={color}> {val} </ColoredText>;

    return { tooltip: title, value: iconInstance };
};

const getBalanceSheetReportSectionItemsForRender = (isLiability?: boolean) => {
    return (items: ISelectItem[]) => {
        return items.filter(item => {
            const section = item.additionalData as IFinancialReportLayoutSectionEntity;
            const noNumberCode = isLiability ? ReportSectionTypeCode.BS_L : ReportSectionTypeCode.BS_A;

            return ((section.ReportSectionType?.Code === noNumberCode && !!section.Number) || (section.ReportSectionType?.Code === ReportSectionTypeCode.BS_S))
                // only include sections that are part of FinancialReportLayout
                && (!section.FinancialReportLayout.DateValidTo || getUtcDayjs(section.FinancialReportLayout.DateValidTo).isSameOrBefore(getUtcDayjs(), "day"));
        }).map(item => {
            item.isDisabled = item.children?.length > 0;
            return item;
        });
    };
};


const getIncomeStatementReportSectionItemsForRender = (isByNatureReportSection?: boolean) => {
    return (items: ISelectItem[], args: IGetValueArgs) => {
        const account = args.storage.data.entity as IAccountEntity;
        const type = account?.Type?.Code;
        let code: ReportSectionTypeCode;

        if (type === AccountTypeCode.Income) {
            code = isByNatureReportSection ? ReportSectionTypeCode.IS_N_I : ReportSectionTypeCode.IS_F_I;
        } else {
            code = isByNatureReportSection ? ReportSectionTypeCode.IS_N_E : ReportSectionTypeCode.IS_F_E;
        }


        return items.filter(item => {
            const section = item.additionalData as IFinancialReportLayoutSectionEntity;
            return section?.ReportSectionType?.Code === code;
        }).map(item => {
            item.isDisabled = item.children?.length > 0;
            return item;
        });
    };
};

export const genericStatusFormatter = (isPositive: boolean): TCellValue => {
    const title = i18next.t(`Common:General.${isPositive ? "Yes" : "No"}`);

    const props = {
        width: `${IconSize.asNumber("S") * 2}px`,
        height: IconSize.S
    };

    const iconInstance = isPositive ?
        <StatusYesIcon {...props} /> :
        <StatusNoIcon {...props} />;

    return { tooltip: title, value: iconInstance };
};

const closedFormatter = (val: TValue): TCellValue => {
    return genericStatusFormatter(val as boolean);
};

export const activeFormatter: TFormatterFn = (val: TValue, values: IFormatOptions): TCellValue => {
    const isActive = values.entity.IsActive;

    return genericStatusFormatter(isActive);
};

const isParentInvertible = (args: IGetValueArgs) => {
    const storage = args.storage as FormStorage<IAccountEntity, IChartOfAccountsFormCustomData>;

    return storage.getCustomData().isParentInvertible;
};

const getIncomeStatementLayoutField = () => ({
    type: FieldType.HierarchyComboBox,
    width: BasicInputSizes.XL,
    isVisible: (args: IGetValueArgs) => {
        const type = args.storage.getValueByPath("Type/Code", { useDirectValue: false }) as string;
        return type === AccountTypeCode.Expense || type === AccountTypeCode.Income;
    },
    additionalProperties: [
        { id: "Name" }
    ],
    columns: [
        {
            id: "Number",
            additionalProperties: [{
                id: "/Name"
            }],
            formatter: AccountDefFormatter
        }
    ],
    // formatter requires Name and Number, if this is for some reason needed, we need to provide Name and Number when we load parent in handleChange
    //formatter: AccountDefFormatter,
    filter: {
        select: (args: IGetValueArgs) => {
            // this filter is shared with templates where there is no company in context
            // but in template company is stored in custom data (based by type of template)
            const accCode = (args.storage as FormStorage<IAccountEntity, IChartOfAccountsFormCustomData>).getCustomData().accountingCode;
            const correctAccCode = accCode ?? args.storage.context.getCompany()?.AccountingCode;

            return `${FinancialReportLayoutSectionEntity.FinancialReportLayout}/${FinancialReportLayoutEntity.AccountingCode} eq '${correctAccCode}'`;
        }
    },
    fieldSettings: {
        preloadItems: true, // TODO: fix setting value from parent, and then remove
        noRecordText: i18next.t("Common:Select.NoRecord"),
        displayName: "Number",
        localDependentFields: [
            { from: { id: "Name" }, to: { id: "Name" }, navigateFrom: NavigationSource.Itself }
        ],
        additionalProperties: [
            { id: "ReportSectionType" },
            { id: "FinancialReportLayout/DateValidTo" }
        ],
        transformFetchedItems: (items: ISelectItem[]): ISelectItem[] => {
            return items.filter(item => {
                // remove root level items that are not used here instead of in itemsForRender,
                // to have correct indent value on items
                const section = item.additionalData as IFinancialReportLayoutSectionEntity;

                return !!section.Number || section.ReportSectionType?.Code === ReportSectionTypeCode.BS_S;
            });
        }
    }
});

const getCategoryItems = (disableIsInvertible: boolean) => {
    return [
        {
            id: CategoryItems.BalanceAssets,
            label: i18next.t("ChartsOfAccounts:Items.Balance"),
            tabularData: [i18next.t("ChartsOfAccounts:Items.Balance"), i18next.t("ChartsOfAccounts:Items.Active"), "—"]
        },
        {
            id: CategoryItems.BalanceLiability,
            label: i18next.t("ChartsOfAccounts:Items.Balance"),
            tabularData: [i18next.t("ChartsOfAccounts:Items.Balance"), i18next.t("ChartsOfAccounts:Items.Liability"), "—"]
        },
        {
            id: CategoryItems.BalanceLiabilityAssets,
            label: i18next.t("ChartsOfAccounts:Items.Balance"),
            tabularData: [i18next.t("ChartsOfAccounts:Items.Balance"), i18next.t("ChartsOfAccounts:Items.ActiveAndLiability"), "—"],
            isDisabled: disableIsInvertible
        },
        {
            id: CategoryItems.IncomeStatementExpense,
            label: i18next.t("ChartsOfAccounts:Items.Statement"),
            tabularData: [i18next.t("ChartsOfAccounts:Items.Statement"), i18next.t("ChartsOfAccounts:Items.Expense"), i18next.t("ChartsOfAccounts:NonTaxable")]
        },
        {
            id: CategoryItems.IncomeStatementExpenseTaxable,
            label: i18next.t("ChartsOfAccounts:Items.Statement"),
            tabularData: [i18next.t("ChartsOfAccounts:Items.Statement"), i18next.t("ChartsOfAccounts:Items.Expense"), i18next.t("ChartsOfAccounts:Taxable")]
        },
        {
            id: CategoryItems.IncomeStatementActive,
            label: i18next.t("ChartsOfAccounts:Items.Statement"),
            tabularData: [i18next.t("ChartsOfAccounts:Items.Statement"), i18next.t("ChartsOfAccounts:Items.Income"), i18next.t("ChartsOfAccounts:NonTaxable")]
        },
        {
            id: CategoryItems.IncomeStatementActiveTaxable,
            label: i18next.t("ChartsOfAccounts:Items.Statement"),
            tabularData: [i18next.t("ChartsOfAccounts:Items.Statement"), i18next.t("ChartsOfAccounts:Items.Income"), i18next.t("ChartsOfAccounts:Taxable")]
        }
    ];
};

const getFinancialStatementCategoryItems = () => [{
    id: CategoryItems.FinancialStatement,
    label: i18next.t("ChartsOfAccounts:Items.FinancialStatement"),
    tabularData: [i18next.t("ChartsOfAccounts:Items.FinancialStatement"), "—", "—"]
}];

const getInvertibleItems = () => {
    return [{
        id: "A",
        label: i18next.t("ChartsOfAccounts:Items.Active")
    }, {
        id: "L",
        label: i18next.t("ChartsOfAccounts:Items.Liability")
    }];
};

const isPostedOrMandatory = (args: IGetValueArgs): boolean => {
    const storage = args.storage as FormStorage<IAccountEntity, IChartOfAccountsFormCustomData>;
    const isPosted = storage.getCustomData().isPosted;
    const isMandatory = storage.getCustomData().isMandatory;
    return isPosted || isMandatory;
};

const isMandatory = (args: IGetValueArgs): boolean => {
    const storage = args.storage as FormStorage<IAccountEntity, IChartOfAccountsFormCustomData>;
    return !!storage.getCustomData().isMandatory;
};

const hasChildWithJournalEntry = (args: IGetValueArgs) => {
    const storage = args.storage as FormStorage<IAccountEntity, IChartOfAccountsFormCustomData>;
    return !!storage.getCustomData().journalData?.DescendantsHaveJournalEntries;
};


const hasJournalEntries = (args: IGetValueArgs): boolean => {
    const storage = args.storage as FormStorage<IAccountEntity, IChartOfAccountsFormCustomData>;
    const journalData = storage.getCustomData().journalData;

    // AccountCategoryCode.IncomeStatement shouldn't have continuity through fiscal years => DEV-10763
    return journalData?.HasJournalEntries ||
        (args.storage.data.entity?.Category?.Code !== AccountCategoryCode.IncomeStatement && journalData?.HasJournalEntriesInDifferentFiscalYear);
};

const tableReportSectionDef = (path: string): TFieldDefinition => ({
    additionalProperties: [{
        id: FinancialReportLayoutSectionEntity.Name
    }, {
        id: FinancialReportLayoutSectionEntity.Number
    }],
    formatter: (val, args): TCellValue => {
        const section = path.split("/").reduce((e, p) => {
            return e?.[p];
        }, args.entity) as IFinancialReportLayoutSectionEntity;
        if (isObjectEmpty(section)) {
            return null;
        }

        if (isNotDefined(section.Number)) {
            return section.Name ?? "";
        }
        return `${section.Number} - ${section.Name}`;
    }
});

export const getDefinitions = (context?: IAppContext): IDefinition => {
    const balanceSheetLayout = AccountEntity.BalanceSheetLayout;
    const incomeStatementLayout = AccountEntity.IncomeStatementLayout;

    const isNonProfit = isNonProfitCompany(context);

    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        defaultFilters: [
            AccountEntity.Number,
            AccountEntity.Name,
            AccountEntity.Category,
            AccountEntity.Type,
            AccountEntity.IsActive,
            AccountEntity.IsClosed
        ],
        filterDefinition: {
            Number: { description: false },
            Detail: { description: false },
            Name: { description: false },
            ...withDisplayName(AccountEntity.Category, { description: false }),
            ...withDisplayName(AccountEntity.Type, { description: false }),
            IsActive: { description: false },
            IsClosed: {
                type: FieldType.ValueHelper,
                description: false
            },
            Description: { description: false },
            ...withDisplayName(AccountEntity.Parent, { description: false }),
            ...withDisplayName(AccountEntity.TaxApplicability, { description: false }),
            ...getCommonFilterDefs({ description: false })
        },
        isValueHelp: true
    }];

    if (isNonProfit) {
        filterBarDef[0].filterDefinition = {
            ...filterBarDef[0].filterDefinition,
            ...withDisplayName(AccountEntity.ActivityType, { description: false })
        };
    }

    const parentDefinition = {
        sort: [{
            id: "FiscalYear/DateStart",
            sort: Sort.Asc
        }],
        columns: [
            { id: "Name" },
            { id: "FiscalYear/Number" },
            { id: "FiscalYear/StatusCode" }
        ],
        entitySet: EntitySetName.ChartsOfAccounts
    };

    const table: ISplitPageTableDef = {
        parentDefinition,
        filterBarDef,
        id: `${EntityTypeName.ChartOfAccounts}Table`,
        columns: [
            // Parent has to be defined here, before Name, to be used in Excel export in correct position
            "Parent",
            "Name",
            "Number",
            "Category",
            "Type",
            "TaxApplicability",
            "Description",
            "IsActive",
            "IsClosed"
        ],
        columnDefinition: {
            ...withDisplayName(AccountEntity.Parent, {
                fieldVisibility: FieldVisibility.ExportOnly
            }),
            Name: {},
            Number: {},
            ...withDisplayName(AccountEntity.Category),
            ...withDisplayName(AccountEntity.Type, {
                formatter: typeFormatter
            }),
            Description: {},
            Detail: {},
            IsActive: {
                formatter: activeFormatter,
                textAlign: TextAlign.Center
            },
            IsClosed: {
                label: i18next.t("ChartsOfAccounts:Closed"),
                formatter: closedFormatter,
                textAlign: TextAlign.Center
            },
            TaxApplicability: {
                additionalProperties: [{
                    id: "/Accounts/Category"
                }],
                formatter: (val): TCellValue => {
                    if (isNotDefined(val)) {
                        return DASH_CHARACTER;
                    }
                    return i18next.t(`ChartsOfAccounts:${val === TaxApplicabilityCode.TaxApplicable ? "Taxable" : "NonTaxable"}`).toString();
                }
            },
            [createPath(AccountEntity.IncomeStatementLayout, IncomeStatementLayoutEntity.ByFunctionReportSection)]: {
                ...tableReportSectionDef(createPath(AccountEntity.IncomeStatementLayout, IncomeStatementLayoutEntity.ByFunctionReportSection)),
                customizationData: {
                    useForCustomization: not(isNonProfit)
                }
            },
            [createPath(AccountEntity.IncomeStatementLayout, IncomeStatementLayoutEntity.ByNatureReportSection)]: {
                ...tableReportSectionDef(createPath(AccountEntity.IncomeStatementLayout, IncomeStatementLayoutEntity.ByNatureReportSection))
            },
            [createPath(AccountEntity.BalanceSheetLayout, BalanceSheetLayoutEntity.AssetsReportSection)]: {
                label: i18next.t("ChartsOfAccounts:BalanceLayout.Active"),
                ...tableReportSectionDef(createPath(AccountEntity.BalanceSheetLayout, BalanceSheetLayoutEntity.AssetsReportSection))
            },
            [createPath(AccountEntity.BalanceSheetLayout, BalanceSheetLayoutEntity.LiabilitiesReportSection)]: {
                label: i18next.t("ChartsOfAccounts:BalanceLayout.Liability"),
                ...tableReportSectionDef(createPath(AccountEntity.BalanceSheetLayout, BalanceSheetLayoutEntity.LiabilitiesReportSection))
            },
            ...getCommonTableColumnsDefs(),
            ...withDisplayName(AccountEntity.ActivityType, {
                customizationData: {
                    useForCustomization: isNonProfit
                }
            })
        },
        tabs: [],
        title: i18next.t("ChartsOfAccounts:Title"),
        initialSortBy: [{ id: "Number", sort: Sort.Asc }],
        hierarchy: "Children"
    };

    let accountDef;

    const summary: ISummaryItem[] = [];

    const incomeStatementLayoutField = getIncomeStatementLayoutField();

    const form: IFormDef = {
        id: `${EntityTypeName.ChartOfAccounts}Form`,
        summary,
        title: i18next.t("ChartsOfAccounts:Account"),
        translationFiles: getDefinitions.translationFiles,
        formControl: ChartOfAccountsFormView,
        isDeletable: true,
        getItemBreadCrumbText: (storage: Model) =>
            getItemBreadCrumbsText(storage, i18next.t("ChartsOfAccounts:NewAccount"), storage.data.entity?.Name),
        isReadOnly: (args: IGetValueArgs) => {
            const storage = args.storage as FormStorage<IAccountEntity, IChartOfAccountsFormCustomData>;
            if (storage.data.entity.CategoryCode === AccountCategoryCode.FinancialStatement) {
                return true;
            }
            // system entities are readOnly (id < 0)
            const chartsOfAccounts: IChartOfAccountsEntity[] = storage.getCustomData().chartOfAccounts;
            const coaId = storage.data.bindingContext?.getParent()?.getKey();
            const selectedChart = chartsOfAccounts?.find(coa => coa.Id === coaId);
            return selectedChart?.FiscalYear?.StatusCode === FiscalYearStatusCode.Closed;
        },
        additionalProperties: [{
            id: "Category"
        }, {
            id: "Type"
        }, {
            id: "TaxApplicabilityCode"
        }, {
            id: "IsInvertible"
        }, {
            id: "Parent",
            additionalProperties: [{
                id: "IsInvertible"
            }]
        }, {
            id: "Children"
        }],
        fieldDefinition: {
            Parent: {
                ...(accountDef = getAccountDef("/Parent/Number")),
                // content of this select might be changed directly in the form
                formatter: AccountDefFormatter,
                cacheStrategy: CacheStrategy.Route,
                width: BasicInputSizes.XL,
                fieldSettings: {
                    ...accountDef.fieldSettings,
                    noRecordText: i18next.t("Common:Select.NoRecord"),
                    entitySet: (args: IGetValueArgs) => {
                        return args.bindingContext.getParent().removeKey().toString();
                    },
                    transformFetchedItems: (items) => {
                        return items.map(item => {
                            if (accountCannotBeParent(item.additionalData as IAccountEntity)) {
                                item.isDisabled = true;
                            }


                            return item;
                        });
                    }
                },
                filter: {
                    select: (args: IGetValueArgs) => {
                        const filters: string[] = [
                            `${AccountEntity.CategoryCode} ne '${AccountCategoryCode.FinancialStatement}'`
                        ];
                        if (args.data.Id) {
                            filters.push(`Id ne ${args.data.Id}`);
                        }
                        return filters.join(" AND ");
                    }
                },
                additionalProperties: [
                    { id: AccountEntity.Name },
                    { id: AccountEntity.IsBankPostable }
                ],
                isDisabled: (args) => {
                    if (args.storage.data.bindingContext.isNew()) {
                        return false;
                    }
                    const entity = args.storage.getEntity<IAccountEntity>();

                    return !!entity.Children?.length || DISABLED_PARENT_PREFIX.includes(entity.Parent?.Number);
                },
                isReadOnly: ifAny(hasJournalEntries, isPostedOrMandatory, hasChildWithJournalEntry),
                affectedFields: [
                    {
                        id: "Number",
                        revalidate: true
                    }
                ]
            },
            Number: {
                width: FastEntryInputSizes.XS,
                formatter: (val: TValue, { entity }: IFormatOptions): string => {
                    const value = val?.toString() ?? "";
                    if (entity.Parent?.Number) {
                        return value.slice(entity.Parent.Number.length);
                    }
                    return value;
                },
                parser: (val: string, { entity }: IFormatOptions): TValue => {
                    if (entity.Parent?.Number) {
                        return `${entity.Parent.Number}${val}`;
                    }
                    return val;
                },
                validator: {
                    type: ValidatorType.String,
                    settings: {
                        numberString: true,
                        customValidator: (value: TValue, args: IGetValueArgs): TValidatorFnResult => {
                            const hasParent = !!(args.storage.data.entity as IAccountEntity).Parent;

                            if (!hasParent && value?.toString().length > 3) {
                                return new ValidationError(i18next.t(`ChartsOfAccounts:Validation.SyntheticAccountNumberLength`), value, args.bindingContext.getPath());
                            }

                            return true;
                        }
                    }
                },
                isReadOnly: ifAny(hasJournalEntries, isPostedOrMandatory, hasChildWithJournalEntry),
                extraFieldContent: ({ storage }) => {
                    const { Parent } = storage.data.entity;
                    const hasParent = !isObjectEmpty(Parent);
                    return hasParent ? (<Text>{Parent.Number}</Text>) : null;
                }
            },
            Name: {
                width: BasicInputSizes.XL,
                isReadOnly: ifAny(hasJournalEntries, isPostedOrMandatory, hasChildWithJournalEntry)
            },
            Description: {
                width: BasicInputSizes.XL
            },
            Detail: {
                width: BasicInputSizes.XL
            },
            [CATEGORY_SELECT_PATH]: {
                isReadOnly: ifAny(hasJournalEntries, isPostedOrMandatory, isParentInvertible, hasChildWithJournalEntry),
                isRequired: true,
                label: i18next.t("ChartsOfAccounts:Category"),
                type: FieldType.ComboBox,
                width: BasicInputSizes.XL,
                fieldSettings: {
                    displayName: "Category",
                    shouldDisplayAdditionalColumns: true,
                    itemsFactory: async (args: IGetValueArgs): Promise<ISelectItem[]> => {
                        const storage = args.storage as FormStorage<IAccountEntity, IChartOfAccountsFormCustomData>;
                        if (storage.data.entity.CategoryCode === AccountCategoryCode.FinancialStatement) {
                            return getFinancialStatementCategoryItems();
                        }
                        return getCategoryItems(storage.getCustomData().journalData?.DescendantsHaveJournalEntries);
                    }
                },
                columns: [{
                    id: "Category",
                    label: i18next.t("ChartsOfAccounts:Category")
                }, {
                    id: "Type",
                    label: i18next.t("ChartsOfAccounts:Type")
                }, {
                    id: "Taxable",
                    label: i18next.t("ChartsOfAccounts:Taxable")
                }],
                affectedFields: [{
                    id: `${balanceSheetLayout}/LiabilitiesReportSection`
                }, {
                    id: `${balanceSheetLayout}/AssetsReportSection`
                }, {
                    id: `${balanceSheetLayout}/IsAssetsCorrection`
                }, {
                    id: BindingContext.localContext("IsInvertible")
                }, {
                    id: `${incomeStatementLayout}/ByNatureReportSection`
                }, {
                    id: `${incomeStatementLayout}/ByFunctionReportSection`
                }],
                comparisonFunction: (entity1: IEntity, entity2: IEntity, bc: BindingContext): boolean => {
                    return composeCategory(entity1) === composeCategory(entity2);
                }
            },
            [BindingContext.localContext("IsInvertible")]: {
                isReadOnly: ifAny(hasJournalEntries, isPostedOrMandatory, isParentInvertible),
                type: FieldType.ComboBox,
                label: i18next.t("ChartsOfAccounts:BalanceLayout.ActiveLiability"),
                fieldSettings: {
                    items: getInvertibleItems()
                },
                isVisible: (args: IGetValueArgs) => {
                    const category = args.storage.getValueByPath(CATEGORY_SELECT_PATH, { useDirectValue: false }) as string;
                    return category === CategoryItems.BalanceLiabilityAssets;
                },
                defaultValue: AccountTypeCode.Assets
            },
            [`${balanceSheetLayout}/IsAssetsCorrection`]: {
                type: FieldType.Checkbox,
                labelStatus: LabelStatus.Removed,
                label: i18next.t("ChartsOfAccounts:BalanceLayout.Correction"),
                isVisible: (args: IGetValueArgs) => {
                    // isInvertible === 'Assets&Liabilities' selected
                    const isInvertible = args.storage.getValueByPath("IsInvertible");
                    const type = args.storage.getValueByPath("Type/Code", { useDirectValue: false }) as string;

                    return type === AccountTypeCode.Liabilities && !isInvertible;
                },
                affectedFields: [{ id: createPath(balanceSheetLayout, BalanceSheetLayoutEntity.LiabilitiesReportSection) },
                    { id: createPath(balanceSheetLayout, BalanceSheetLayoutEntity.AssetsReportSection) }],
                isReadOnly: isParentInvertible
            },
            [`${balanceSheetLayout}/LiabilitiesReportSection`]: {
                ...incomeStatementLayoutField,
                label: i18next.t("ChartsOfAccounts:BalanceLayout.Balance"),
                isVisible: (args: IGetValueArgs) => {
                    const type = args.storage.getValueByPath("Type/Code");
                    const isInvertible = args.storage.getValueByPath("IsInvertible");
                    const isLiability = type === AccountTypeCode.Liabilities;
                    const isCorrection = args.storage.getValueByPath(createPath(balanceSheetLayout, BalanceSheetLayoutEntity.IsAssetsCorrection));

                    return (isLiability && !isCorrection) || !!isInvertible;
                },
                isReadOnly: false,
                fieldSettings: {
                    ...incomeStatementLayoutField.fieldSettings,
                    itemsForRender: getBalanceSheetReportSectionItemsForRender(true)
                }
            },
            [`${balanceSheetLayout}/AssetsReportSection`]: {
                ...incomeStatementLayoutField,
                label: i18next.t("ChartsOfAccounts:BalanceLayout.Balance"),
                isVisible: (args: IGetValueArgs) => {
                    const type = args.storage.getValueByPath("Type/Code");
                    const isInvertible = args.storage.getValueByPath("IsInvertible");
                    const isAsset = type === AccountTypeCode.Assets;
                    const isCorrection = args.storage.getValueByPath(createPath(balanceSheetLayout, BalanceSheetLayoutEntity.IsAssetsCorrection));
                    const isLiability = type === AccountTypeCode.Liabilities;

                    return isAsset || !!isInvertible || !!(isLiability && isCorrection);
                },
                isReadOnly: false,
                fieldSettings: {
                    ...incomeStatementLayoutField.fieldSettings,
                    itemsForRender: getBalanceSheetReportSectionItemsForRender()
                }
            },
            [`${incomeStatementLayout}/ByNatureReportSection`]: {
                ...incomeStatementLayoutField,
                isReadOnly: isParentInvertible,
                fieldSettings: {
                    ...incomeStatementLayoutField.fieldSettings,
                    itemsForRender: getIncomeStatementReportSectionItemsForRender(true)
                }
            },

            [`${incomeStatementLayout}/ByFunctionReportSection`]: {
                ...incomeStatementLayoutField,
                fieldSettings: {
                    ...incomeStatementLayoutField.fieldSettings,
                    itemsForRender: getIncomeStatementReportSectionItemsForRender()
                },
                customizationData: {
                    useForCustomization: not(isNonProfit)
                },
                isReadOnly: isParentInvertible,
                isVisible: (args: IGetValueArgs) => {
                    const cat = args.storage.getValueByPath(CATEGORY_SELECT_PATH);
                    const isStatement = isIncomeStatementCategory(cat);
                    return !isNonProfitCompanyWithTemplateFallback(args) && isStatement;
                }
            },
            ActivityType: {
                type: FieldType.ComboBox,
                defaultValue: AccountActivityTypeCode.Main,
                fieldSettings: {
                    displayName: "Name"
                },
                customizationData: {
                    useForCustomization: isNonProfit
                },
                isRequired: true,
                isVisible: (args: IGetValueArgs) => {
                    const account = args.storage.getEntity<IAccountEntity>();
                    return isNonProfitCompanyWithTemplateFallback(args) && account.CategoryCode === AccountCategoryCode.IncomeStatement;
                }
            },
            IsActive: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.Icons
                },
                isDisabled: isMandatory,
                defaultValue: true
            },
            IsClosed: {
                label: i18next.t("ChartsOfAccounts:Closed"),
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.Icons
                }
            },
            IsBankPostable: {
                type: FieldType.Switch,
                fieldSettings: {
                    type: SwitchType.YesNo
                }
            }
        },
        groups: [{
            id: "mainGroup",
            rows: [
                [{ id: AccountEntity.Parent }, { id: AccountEntity.Number }, { id: AccountEntity.Name }], [{ id: AccountEntity.Description }],
                [{ id: AccountEntity.IsBankPostable }]
            ]
        }, {
            id: "balance",
            title: i18next.t("ChartsOfAccounts:Balance"),
            rows: [
                [{ id: CATEGORY_SELECT_PATH }, { id: BindingContext.localContext("IsInvertible") }],
                [{ id: `${balanceSheetLayout}/AssetsReportSection` }, { id: `${balanceSheetLayout}/LiabilitiesReportSection` }, { id: `${balanceSheetLayout}/IsAssetsCorrection` }],
                [{ id: `${incomeStatementLayout}/ByNatureReportSection` }, { id: `${incomeStatementLayout}/ByFunctionReportSection` }, { id: `ActivityType` }],
            ]
        }, {
            id: "type",
            title: i18next.t("ChartsOfAccounts:State"),
            rows: [[{ id: AccountEntity.IsActive }, { id: AccountEntity.IsClosed }]]
        }]
    };

    const getEntitySet = (args: IGetValueArgs) => {
        const activeCOAId = args.data?.parentId;

        if (!activeCOAId) {
            return undefined;
        }

        return `${EntitySetName.ChartsOfAccounts}(${activeCOAId})/Accounts`;
    };

    return {
        entitySet: getEntitySet,
        table,
        form
    };
};

getDefinitions.translationFiles = ["ChartsOfAccounts", "Common"];
setDefByEntityType(EntityTypeName.Account, getDefinitions);